/**
 * @module Button
 *
 * Versatile button component with multiple variants, sizes, and responsive options.
 * Supports all standard button interactions plus loading states and icon-only modes.
 *
 * @see {@link https://ui.shadcn.com/docs/components/button Shadcn Button}
 *
 * @example
 * // Basic buttons
 * <Button>Default</Button>
 * <Button variant="brand">Primary Action</Button>
 * <Button variant="subtle">Secondary</Button>
 *
 * @example
 * // Sizes
 * <Button size="xs">Extra Small</Button>
 * <Button size="sm">Small</Button>
 * <Button size="lg">Large</Button>
 * <Button size="xl">Extra Large</Button>
 *
 * @example
 * // Responsive size (changes at breakpoints)
 * <Button size={{ default: "sm", md: "default", lg: "lg" }}>
 *   Responsive Button
 * </Button>
 *
 * @example
 * // Icon button
 * <Button icon size="default"><PlayIcon /></Button>
 *
 * @example
 * // As link (using asChild)
 * <Button asChild variant="brand">
 *   <a href="/signup">Sign Up</a>
 * </Button>
 *
 * @example
 * // With loading state
 * <Button disabled>
 *   <Loading visible={isLoading} />
 *   {isLoading ? "Loading..." : "Submit"}
 * </Button>
 */
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { Slot as SlotPrimitive } from "radix-ui";
import type { ButtonHTMLAttributes, Ref } from "react";
import { cn } from "../lib/utils";

/** Size classes for consistent button dimensions */
// Define size classes once as a single source of truth (including leading but not font sizes).
//
// Every size must set the same properties — width, gap and both paddings as well
// as height and leading — even where the value is the default. A responsive
// `size` is applied by prefixing these classes per breakpoint and appending
// them, so anything one size sets and another omits is never unset: an
// `{ default: "icon", sm: "lg" }` button kept `w-8` past `sm` and squeezed its
// label into a vertical column of letters.
const sizeClasses = {
  default: "h-8 w-auto gap-2 px-4 py-2 leading-8",
  xs: "h-5 w-auto gap-1.5 px-2 py-0 leading-5",
  sm: "h-7 w-auto gap-2 px-3 py-2 leading-6",
  lg: "h-10 w-auto gap-2 px-4 py-2 leading-8",
  xl: "h-12 w-auto gap-2 px-8 py-3 leading-10",
  icon: "h-8 w-8 gap-2 px-0 py-0 leading-8"
} as const;
const fontSizeClasses = {
  xxs: "text-xxs",
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg",
  xl: "text-xl"
} as const;

// Type definitions for responsive sizes
type ButtonSize = "default" | "xs" | "sm" | "lg" | "icon" | "xl";
type ButtonFontSize = "xxs" | "xs" | "sm" | "base" | "lg" | "xl" | null;

// Default font size mapping based on button size (excludes null)
const defaultFontSizeForSize: Record<ButtonSize, Exclude<ButtonFontSize, null>> = {
  xs: "xs",
  sm: "xs",
  default: "base",
  lg: "base",
  icon: "base",
  xl: "lg"
};
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap font-normal font-sans text-base transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--focus-ring-offset)] enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50", {
  variants: {
    variant: {
      // BRANDING VARIANTS (use CSS variables for themeability)
      subtle: "border-1 border-gray-150 border-solid bg-white text-gray-950 hover:bg-gray-50 active:border-brand-primary-hover active:bg-brand-secondary active:text-brand-primary-hover disabled:bg-gray-50 active:disabled:border-gray-150 active:disabled:bg-gray-50 active:disabled:text-gray-950 dark:border-gray-800 dark:bg-gray-950 dark:text-white dark:disabled:border-gray-900 dark:disabled:bg-gray-950 dark:disabled:text-white dark:active:border-white dark:active:text-white dark:disabled:active:border-gray-900 dark:disabled:active:bg-gray-950 dark:disabled:active:text-white dark:hover:bg-gray-800",
      brand: "border-1 border-brand-primary border-solid bg-brand-primary font-semibold text-white hover:border-brand-primary-hover hover:bg-brand-primary-hover disabled:hover:bg-brand-primary-hover",
      brandSecondary: "border-1 border-brand-secondary border-solid bg-brand-secondary font-semibold text-brand-primary-hover hover:border-brand-secondary-hover hover:bg-brand-secondary-hover disabled:hover:bg-brand-secondary-hover dark:border-gray-900 dark:bg-gray-900 hover:dark:border-gray-800 hover:dark:bg-gray-800 dark:text-white dark:disabled:hover:border-gray-900 dark:disabled:hover:bg-gray-800",
      bold: "border-gray-950 bg-gray-950 font-semibold text-white dark:border-white dark:bg-white dark:text-gray-950",
      light: "border-gray-150 bg-gray-100 font-semibold text-gray-950 hover:border-gray-200 hover:bg-gray-200 disabled:hover:border-gray-150 disabled:hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 dark:disabled:hover:bg-gray-800",
      transparent: "border-transparent! bg-transparent font-semibold text-gray-950 hover:border-gray-50 hover:bg-gray-50 disabled:hover:border-transparent disabled:hover:bg-transparent dark:text-white dark:hover:border-gray-900 dark:hover:bg-gray-800",
      outline: "border-1 border-pink-100 !border-solid text-brand-primary hover:bg-pink-50 disabled:hover:bg-transparent dark:text-white dark:border-[#74163B] dark:hover:bg-pink-900 dark:disabled:hover:bg-transparent",
      contrast: "border-white bg-white font-semibold text-gray-950 hover:bg-gray-50 hover:disabled:border-white hover:disabled:bg-white",
      success: "border-green-600 bg-green-600 font-semibold text-white hover:border-green-700 hover:bg-green-700 disabled:hover:border-green-600 disabled:hover:bg-green-600 dark:border-green-500 dark:bg-green-500 dark:hover:border-green-650 dark:hover:bg-green-650 dark:disabled:hover:border-green-500 dark:disabled:hover:bg-green-500",
      error: "border-red-600 bg-red-600 font-semibold text-white hover:bg-red-700 disabled:hover:border-red-700 disabled:hover:bg-red-700 dark:border-red-500 dark:bg-red-500 dark:hover:border-red-600 dark:hover:bg-red-600 dark:disabled:hover:border-red-600 dark:disabled:hover:bg-red-600"
    },
    size: sizeClasses,
    fontSize: {
      ...fontSizeClasses,
      null: "" // No font size class, relies on compound variants
    },
    borderRadius: {
      default: "rounded-lg",
      sm: "rounded",
      full: "rounded-full",
      rounded: "rounded-[100px]"
    },
    border: {
      true: "border border-solid",
      false: "border-none"
    },
    noFeedback: {
      true: "",
      false: "transition-transform active:scale-[0.97] disabled:scale-100"
    }
  },
  compoundVariants: [
  // Size + BorderRadius compounds
  {
    size: "sm",
    borderRadius: "default",
    className: "rounded-lg"
  }, {
    size: "xs",
    borderRadius: "default",
    className: "rounded-md"
  },
  // Variant + Border compounds
  {
    variant: "transparent",
    border: true,
    className: "border-gray-200 dark:border-gray-800"
  }, {
    variant: "success",
    border: true,
    className: "border-green-dark"
  }, {
    variant: "contrast",
    border: true,
    className: "border-white"
  },
  // Size + fontSize compounds (default font size for each size when no explicit fontSize)
  {
    size: "xs",
    fontSize: null,
    className: "text-xs"
  }, {
    size: "sm",
    fontSize: null,
    className: "text-xs"
  }, {
    size: "default",
    fontSize: null,
    className: "text-base"
  }, {
    size: "lg",
    fontSize: null,
    className: "text-base"
  }, {
    size: "icon",
    fontSize: null,
    className: "text-base"
  }, {
    size: "xl",
    fontSize: null,
    className: "text-lg"
  }],
  defaultVariants: {
    variant: "bold",
    size: "default",
    fontSize: null,
    borderRadius: "default",
    border: false,
    noFeedback: false
  }
});
type ResponsiveButtonSize = ButtonSize | {
  default: ButtonSize;
  xxs?: ButtonSize;
  xs?: ButtonSize;
  sm?: ButtonSize;
  md?: ButtonSize;
  lg?: ButtonSize;
  xl?: ButtonSize;
  "2xl"?: ButtonSize;
};
type ResponsiveButtonFontSize = Exclude<ButtonFontSize, null> | {
  default?: Exclude<ButtonFontSize, null>;
  xxs?: Exclude<ButtonFontSize, null>;
  xs?: Exclude<ButtonFontSize, null>;
  sm?: Exclude<ButtonFontSize, null>;
  md?: Exclude<ButtonFontSize, null>;
  lg?: Exclude<ButtonFontSize, null>;
  xl?: Exclude<ButtonFontSize, null>;
  "2xl"?: Exclude<ButtonFontSize, null>;
};
const getSizeClasses = (sizeKey: ButtonSize): string => sizeClasses[sizeKey] || "";
const getFontSizeClasses = (fontSizeKey: Exclude<ButtonFontSize, null>): string => fontSizeClasses[fontSizeKey] || "";

// Function to generate responsive Tailwind classes
function generateResponsiveSizeClasses(size?: ResponsiveButtonSize): string {
  if (!size || typeof size === "string") {
    return "";
  }
  const classes: string[] = [];

  // Add responsive classes for each breakpoint
  const breakpoints = [{
    prefix: "xxs",
    value: size.xxs
  }, {
    prefix: "xs",
    value: size.xs
  }, {
    prefix: "sm",
    value: size.sm
  }, {
    prefix: "md",
    value: size.md
  }, {
    prefix: "lg",
    value: size.lg
  }, {
    prefix: "xl",
    value: size.xl
  }, {
    prefix: "2xl",
    value: size["2xl"]
  }];
  for (const breakpoint of breakpoints) {
    if (breakpoint.value) {
      const breakpointSizeClasses = getSizeClasses(breakpoint.value);
      const responsiveClasses = breakpointSizeClasses.split(" ").filter(cls => cls.trim() !== "").map(cls => `${breakpoint.prefix}:${cls}`).join(" ");
      if (responsiveClasses) {
        classes.push(responsiveClasses);
      }
    }
  }
  return classes.join(" ");
}
function generateResponsiveFontSizeClasses(fontSize?: ResponsiveButtonFontSize): string {
  if (!fontSize || typeof fontSize === "string") {
    return "";
  }
  const classes: string[] = [];

  // Add responsive classes for each breakpoint
  const breakpoints = [{
    prefix: "xxs",
    value: fontSize.xxs
  }, {
    prefix: "xs",
    value: fontSize.xs
  }, {
    prefix: "sm",
    value: fontSize.sm
  }, {
    prefix: "md",
    value: fontSize.md
  }, {
    prefix: "lg",
    value: fontSize.lg
  }, {
    prefix: "xl",
    value: fontSize.xl
  }, {
    prefix: "2xl",
    value: fontSize["2xl"]
  }];
  for (const breakpoint of breakpoints) {
    if (breakpoint.value) {
      const breakpointFontSizeClasses = getFontSizeClasses(breakpoint.value);
      const responsiveClasses = breakpointFontSizeClasses.split(" ").filter(cls => cls.trim() !== "").map(cls => `${breakpoint.prefix}:${cls}`).join(" ");
      if (responsiveClasses) {
        classes.push(responsiveClasses);
      }
    }
  }
  return classes.join(" ");
}

/**
 * Props for the Button component.
 *
 * @property variant - Color/style: `brand`, `brandSecondary`, `subtle`, `bold`, `light`, `transparent`, `contrast`, `success`, `error`
 * @property size - Size or responsive size object: `xs`, `sm`, `default`, `lg`, `xl`, `icon`
 * @property fontSize - Override font size or responsive font size object
 * @property borderRadius - Corner style: `default`, `sm`, `full`, `rounded`
 * @property border - Show border when true
 * @property noFeedback - Disable scale animation on click
 * @property asChild - Render as child element (e.g., for links)
 * @property icon - Square aspect ratio with no padding (for icon-only buttons)
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, Omit<VariantProps<typeof buttonVariants>, "size" | "fontSize"> {
  asChild?: boolean;
  fontSize?: ResponsiveButtonFontSize;
  icon?: boolean;
  ref?: Ref<HTMLButtonElement>;
  size?: ResponsiveButtonSize;
}
const Button = ({
  className,
  variant,
  size,
  borderRadius,
  border,
  noFeedback,
  asChild = false,
  type,
  children,
  fontSize,
  icon = false,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? SlotPrimitive.Slot : "button";
  const buttonType = asChild ? undefined : type ?? "button";

  // Generate responsive classes or use the cva size variant
  const responsiveClasses = typeof size === "object" ? generateResponsiveSizeClasses(size) : undefined;

  // Generate responsive font size classes
  let responsiveFontSizeClasses: string | undefined;
  if (typeof fontSize === "object") {
    // User provided explicit responsive font sizes
    responsiveFontSizeClasses = generateResponsiveFontSizeClasses(fontSize);
  } else if (typeof size === "object" && fontSize === undefined) {
    // Auto-generate responsive font sizes based on responsive sizes
    const autoFontSize: ResponsiveButtonFontSize = {
      default: defaultFontSizeForSize[size.default] || "base"
    };

    // Map over the size object to create the auto font size object
    const breakpointMappings = [{
      sizeKey: "xxs" as const,
      fontKey: "xxs" as const
    }, {
      sizeKey: "xs" as const,
      fontKey: "xs" as const
    }, {
      sizeKey: "sm" as const,
      fontKey: "sm" as const
    }, {
      sizeKey: "md" as const,
      fontKey: "md" as const
    }, {
      sizeKey: "lg" as const,
      fontKey: "lg" as const
    }, {
      sizeKey: "xl" as const,
      fontKey: "xl" as const
    }, {
      sizeKey: "2xl" as const,
      fontKey: "2xl" as const
    }];
    for (const {
      sizeKey,
      fontKey
    } of breakpointMappings) {
      const sizeValue = (size as Record<string, ButtonSize>)[sizeKey];
      if (sizeValue && sizeValue in defaultFontSizeForSize) {
        (autoFontSize as Record<string, Exclude<ButtonFontSize, null>>)[fontKey] = defaultFontSizeForSize[sizeValue as ButtonSize];
      }
    }
    responsiveFontSizeClasses = generateResponsiveFontSizeClasses(autoFontSize);
  }

  // For object sizes, use the default size in the cva
  const cvaSize = typeof size === "object" ? size.default : size;

  // Determine font size for cva
  let cvaFontSize: ButtonFontSize = null;
  if (fontSize) {
    if (typeof fontSize === "object") {
      // If fontSize object has no default, let compound variants handle it (null)
      cvaFontSize = fontSize.default || null;
    } else {
      cvaFontSize = fontSize;
    }
  } // null triggers compound variants for default font sizes

  // Generate icon classes if icon prop is true
  let iconClasses = "";
  if (icon) {
    iconClasses = "aspect-square p-0";

    // If using responsive sizes, we need to override padding at each breakpoint
    if (typeof size === "object") {
      const iconResponsiveClasses: string[] = [];
      const breakpointMappings = [{
        sizeKey: "xxs" as const,
        prefix: "xxs"
      }, {
        sizeKey: "xs" as const,
        prefix: "xs"
      }, {
        sizeKey: "sm" as const,
        prefix: "sm"
      }, {
        sizeKey: "md" as const,
        prefix: "md"
      }, {
        sizeKey: "lg" as const,
        prefix: "lg"
      }, {
        sizeKey: "xl" as const,
        prefix: "xl"
      }, {
        sizeKey: "2xl" as const,
        prefix: "2xl"
      }];
      for (const {
        sizeKey,
        prefix
      } of breakpointMappings) {
        const sizeValue = (size as Record<string, ButtonSize>)[sizeKey];
        if (sizeValue) {
          iconResponsiveClasses.push(`${prefix}:p-0`);
        }
      }
      if (iconResponsiveClasses.length > 0) {
        iconClasses += ` ${iconResponsiveClasses.join(" ")}`;
      }
    }
  }
  return <Comp className={cn(buttonVariants({
    variant,
    size: cvaSize,
    fontSize: cvaFontSize,
    borderRadius,
    border,
    noFeedback
  }), responsiveClasses, responsiveFontSizeClasses, iconClasses, className)} ref={ref} type={buttonType} {...props} data-sentry-element="Comp" data-sentry-component="Button" data-sentry-source-file="button.tsx">
      {children}
    </Comp>;
};

/** CVA variants for Loading spinner sizing */
const loadingVariants = cva("hidden w-4 animate-[spin_0.75s_linear_infinite] text-inherit", {
  variants: {
    size: {
      default: "w-4",
      lg: "w-5",
      sm: "w-3",
      xs: "w-3",
      icon: "w-4",
      xl: "w-5"
    },
    visible: {
      true: "block",
      false: "hidden"
    }
  },
  defaultVariants: {
    size: "default",
    visible: false
  }
});

/**
 * Animated loading spinner for use inside buttons.
 *
 * @param size - Match parent button size for proper scaling
 * @param visible - Show/hide the spinner
 *
 * @example
 * <Button disabled={isLoading}>
 *   <Loading visible={isLoading} size="default" />
 *   Submit
 * </Button>
 */
const Loading = ({
  size,
  visible
}: VariantProps<typeof loadingVariants>) => <LoaderCircle className={cn(loadingVariants({
  size,
  visible
}))} data-sentry-element="LoaderCircle" data-sentry-component="Loading" data-sentry-source-file="button.tsx" />;
export type { ButtonFontSize, ButtonSize, ResponsiveButtonFontSize, ResponsiveButtonSize };
export { Button, buttonVariants, Loading, loadingVariants };